import axios from "axios";
import useAuthStore from "../store/authStore";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 10000,
  withCredentials: false,
});

const publicApis = [
  "/api/v1/content/list",
  "/api/v1/user/signin",
  "/api/v1/user/send/sms/auth",
  "/api/v1/user/sms/auth",
];

const tokenRefreshApis = ["/api/v1/user/issued/access"];

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else {
      request.resolve(token);
    }
  });
  failedQueue = [];
};

const isPublicApi = (url) => {
  return publicApis.some((api) => url && url.includes(api));
};

const isTokenRefreshApi = (url) => {
  return tokenRefreshApis.some((api) => url && url.includes(api));
};

apiClient.interceptors.request.use(
  async (config) => {
    config.headers["Content-Type"] = "application/json";

    if (!isPublicApi(config.url)) {
      const { userInfo } = useAuthStore.getState();
      if (userInfo?.accessToken) {
        config.headers.Authorization = userInfo.accessToken;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  async (response) => {
    if (isTokenRefreshApi(response.config.url)) {
      return response;
    }

    if (
      response.data &&
      response.data.status === false &&
      response.data.expired === true
    ) {

      const originalRequest = response.config;

      if (originalRequest._retry || isPublicApi(originalRequest.url)) {
        console.log("재시도 제한 또는 public API, 토큰 재발급 안함");
        return Promise.reject(new Error("Token expired"));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = token;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      return handleTokenRefresh(originalRequest);
    }

    return response;
  },
  async (error) => {
    console.log(
      "Response interceptor error:",
      error.response?.status,
      error.message
    );
    const originalRequest = error.config;

    if (isTokenRefreshApi(originalRequest?.url)) {
      return Promise.reject(error);
    }

    const isTokenExpired = error.response?.status === 401;

    if (
      isTokenExpired &&
      !originalRequest._retry &&
      !isPublicApi(originalRequest?.url)
    ) {
      console.log("401 에러 감지, 재발급 시도");

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = token;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      return handleTokenRefresh(originalRequest);
    }

    return Promise.reject(error);
  }
);

async function handleTokenRefresh(originalRequest) {
  originalRequest._retry = true;
  isRefreshing = true;

  const { userInfo, logout, updateUserInfo } = useAuthStore.getState();

  if (!userInfo?.refreshToken) {
    console.log("리프레시 토큰이 없습니다. 로그아웃 처리합니다.");
    processQueue(new Error("No refresh token"), null);
    isRefreshing = false;
    await logout();
    redirectToAuth();
    return Promise.reject(new Error("No refresh token"));
  }

  try {
    const refreshResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/v1/user/issued/access`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: userInfo.refreshToken,
        },
        timeout: 10000,
      }
    );

    if (
      refreshResponse.data &&
      refreshResponse.data.status === true &&
      refreshResponse.data.access_token
    ) {
      const newAccessToken = refreshResponse.data.access_token;

      const currentStorage = localStorage.getItem(
        "dplanit-moming-auth-storage"
      );

      if (currentStorage) {
        const storageData = JSON.parse(currentStorage);
        const newState = {
          ...storageData.state,
          userInfo: {
            ...storageData.state.userInfo,
            accessToken: newAccessToken,
          },
        };
        const newStorageData = {
          state: newState,
          version: 0,
        };
        localStorage.setItem(
          "dplanit-moming-auth-storage",
          JSON.stringify(newStorageData)
        );
      }

      processQueue(null, newAccessToken);
      originalRequest.headers.Authorization = newAccessToken;

      // Zustand 스토어 상태도 업데이트
      updateUserInfo({
        accessToken: newAccessToken,
      });

      isRefreshing = false;
      
      // 페이지 새로고침 대신 원래 요청을 직접 재시도
      console.log("✅ 토큰 재발급 완료, 원래 요청 재시도");
      return apiClient(originalRequest);
    } else {
      console.error("예상치 못한 응답:", refreshResponse.data);
      throw new Error(
        "Token refresh failed - invalid response: " +
          JSON.stringify(refreshResponse.data)
      );
    }
  } catch (refreshError) {
    console.error("❌ 토큰 재발급 실패:", refreshError);

    if (refreshError.expired === true) {
      console.log("Refresh Token이 만료되었습니다.");
    } else if (
      refreshError?.error ===
      "unmatched_authorization_token_in_device"
    ) {
      console.log(
        "디바이스 토큰 불일치 에러 - refresh token이 다른 디바이스에서 발급되었거나 만료됨"
      );
    }

    processQueue(refreshError, null);
    await logout();
    redirectToAuth();

    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

function redirectToAuth() {
  if (typeof window !== "undefined") {
    window.location.href = "/";
  }
}

export { apiClient };
